import {
  Box,
  ButtonGroup,
  Container,
  HStack,
  useDisclosure,
  Collapse,
} from '@chakra-ui/react'
import { MobileDrawer } from './mobileDrawer'
import { ToggleButton } from './toggleButton'
import SearchIcon from '@/components/svg/SearchIcon'
import { Button } from '@/components/atoms/button'
import { ButtonVariants, ButtonSizes } from '@/types/enums'
import { SearchBar } from '@/components/molecules/searchBar'

import Link from 'next/link'
import { useEffect, useState, useRef } from 'react'
import {
  ButtonTypes,
  ComponentProps,
  IconPositions,
  NavigationLevels,
} from '@/baseComponents'
import { buildImageUrl } from '@/utils/imageSharpUrlBuilder'
import { MediaImage } from '@/components/atoms/media'
import { MediaImageData } from '@/components/atoms/media/MediaImage'
import { useTranslations } from 'next-intl'

interface Props {
  menuItems: Array<MenuItem>
  companyLogo?: MediaImageData
  topMenuItems: TopMenuItems
  hasTopLevelNavigation: boolean
  searchPageUrl?: MenuItem[]
  isHomePage?: boolean
}

interface TopLevelNavigationProps {
  topMenuItems: TopMenuItems
  isVisible: boolean
}

interface MobileMenuProps extends ComponentProps {
  mobileNavbar: any
  isVisible: boolean
  menuItems: Array<MenuItem>
  topMenuItems: TopMenuItems
}

type TopMenuItems = {
  left: Array<TopMenuItem>
  right: Array<MenuItem>
}

interface TopMenuItem extends MenuItem {
  isCurrentHome: boolean
}

export type MenuItem = {
  title: any
  label: string
  target: string
  url?: string
}

export const TopLevelNavigation = ({
  topMenuItems,
  isVisible,
}: TopLevelNavigationProps) => {
  return (
    <Box
      bg={'brand.grey'}
      py={2}
      px={{ base: 4, md: 6, lg: 10 }}
      position='relative'
      zIndex='tooltip'
      maxW='none'
    >
      <HStack
        justify='space-between'
        fontSize={'0.875rem'}
        lineHeight={'normal'}
      >
        {/* Top menu left items */}
        <ButtonGroup>
          {topMenuItems?.left?.map((element: TopMenuItem, i: number) => {
            return (
              <Button
                className='topmenuitem'
                key={'TopMenuLeft' + i}
                text={element.label}
                url={element.url}
                buttonType={ButtonTypes.TextOnly}
                buttonVariant={ButtonVariants.Text}
                buttonSize={ButtonSizes.Small}
                navigationTrackingEventProps={{
                  navigationItem: element.label,
                  navigationLevel: NavigationLevels.Top,
                }}
                buttonProps={{
                  tabIndex: isVisible ? -1 : 0,
                  fontSize: '0.875rem',
                  position: 'relative',
                  fontWeight: element.isCurrentHome ? '700' : '400',
                  _before: element.isCurrentHome
                    ? {
                        content: "''",
                        position: 'absolute',
                        width: 4,
                        height: 4,
                        background: 'brand.white',
                        bottom: '-1.2rem',
                        left: 0,
                        right: 0,
                        margin: 'auto',
                        transform: 'rotate(45deg)',
                        zIndex: -1,
                      }
                    : {},
                }}
              />
            )
          })}
        </ButtonGroup>
        {/* Top menu right items */}
        <ButtonGroup display={{ base: 'none', md: 'flex' }}>
          {topMenuItems?.right?.map((element: MenuItem, i: number) => {
            return (
              <Button
                className='topmenuitem'
                key={'TopMenuRight' + i}
                text={element.label}
                url={element.url}
                target={element.target}
                buttonType={ButtonTypes.TextOnly}
                buttonVariant={ButtonVariants.Text}
                buttonSize={ButtonSizes.Small}
                navigationTrackingEventProps={{
                  navigationItem: element.label,
                  navigationLevel: NavigationLevels.Top,
                }}
                buttonProps={{
                  fontSize: '0.875rem',
                  tabIndex: isVisible ? -1 : 0,
                }}
              />
            )
          })}
        </ButtonGroup>
      </HStack>
    </Box>
  )
}

export const CompanyLogo = (companyLogo: MediaImageData) => {
  return (
    <Link
      href={{
        pathname: '/',
      }}
    >
      <MediaImage
        {...companyLogo}
        viewportWidthFactor={{ base: 0.2, lg: 0.85 }}
        cropSizeAlias={{ base: 'logo' }}
        hasBorderRadius={false}
        imageUrlBuilder={buildImageUrl}
        height='44px'
        width='130px'
      />
    </Link>
  )
}
export const MobileMenu = ({
  mobileNavbar,
  isVisible,
  menuItems,
  topMenuItems,
  ...props
}: MobileMenuProps) => {
  return (
    <>
      <ToggleButton
        onClick={mobileNavbar.onToggle}
        isOpen={mobileNavbar.isOpen}
        aria-label='Open Menu'
      />
      <MobileDrawer
        isOpen={mobileNavbar.isOpen}
        onClose={mobileNavbar.onClose}
        isVisible={isVisible}
        menuItems={menuItems}
        topRightItems={topMenuItems?.right}
      />
    </>
  )
}

export const MenuSearchIcon = ({
  searchPageUrl,
}: {
  searchPageUrl?: MenuItem[]
}) => {
  return (
    <ButtonGroup size='sm' spacing={{ base: 4, md: 6 }}>
      {/* This component is kept for compatibility with existing code, but functionality moved to Navigation component */}
      <Button
        buttonType={ButtonTypes.IconOnly}
        buttonVariant={ButtonVariants.Text}
        buttonSize={ButtonSizes.Small}
        icon={<SearchIcon />}
        iconPosition={IconPositions.Right}
        url={searchPageUrl?.[0]?.url}
        navigationTrackingEventProps={{
          navigationLevel: NavigationLevels.Top,
          navigationItem: 'Search',
        }}
      />
    </ButtonGroup>
  )
}

export const Navigation = ({
  menuItems,
  hasTopLevelNavigation = true,
  topMenuItems,
  companyLogo,
  searchPageUrl,
  isHomePage,
}: Props) => {
  const t = useTranslations()
  const mobileNavbar = useDisclosure()
  const [lastScrollY, setLastScrollY] = useState(0)
  const [isTop, setIsTop] = useState(true)
  const [isVisible, setIsVisible] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Focus the search input when it opens
  useEffect(() => {
    // Add a small delay to ensure the Collapse animation has started
    // before attempting to focus, which makes focus more reliable
    if (isSearchOpen && searchInputRef.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isSearchOpen])

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen)
  }

  const handleSearch = () => {
    if (searchInputRef.current && searchInputRef.current.value) {
      const searchValue = searchInputRef.current.value
      const searchUrl = searchPageUrl?.[0]?.url || '/search'
      window.location.href = `${searchUrl}?query=${encodeURIComponent(searchValue)}`
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      const shouldShowTopNavigation = currentScrollY < 5
      setIsTop(shouldShowTopNavigation)
      setIsVisible(shouldShowTopNavigation)

      const shouldHideTopNavigation = currentScrollY > lastScrollY
      setIsVisible(shouldHideTopNavigation)

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [lastScrollY])

  return (
    <Box
      as='section'
      position={isTop ? 'relative' : 'fixed'}
      top={isVisible ? '-2.875rem' : '0'}
      left='0'
      width={'full'}
      transition='top 0.3s'
      zIndex='tooltip'
    >
      {/* Top level navigation  */}
      {hasTopLevelNavigation && (
        <TopLevelNavigation topMenuItems={topMenuItems} isVisible={isVisible} />
      )}
      {/* Main navigation */}
      <Box bg='brand.white' position='relative' zIndex='tooltip'>
        <Container
          py={4}
          px={{ base: 5, md: 8, lg: 10 }}
          maxWidth={'full'}
          fontWeight={'normal'}
        >
          <HStack justify='space-between'>
            <ButtonGroup size='sm'>
              {/* Mobile menu */}
              <Box
                display={{ base: 'flex', lg: 'none' }}
                alignItems='center'
                mr={6}
              >
                <MobileMenu
                  mobileNavbar={mobileNavbar}
                  menuItems={menuItems}
                  topMenuItems={topMenuItems}
                  isVisible={isVisible}
                />
              </Box>
              {/* Logo */}
              {companyLogo && <CompanyLogo {...companyLogo} />}
            </ButtonGroup>
            <HStack gap={6}>
              {/* Menu items */}
              <ButtonGroup
                size='sm'
                spacing={{ lg: 1, xl: 6 }}
                display={{ base: 'none', lg: 'flex' }}
              >
                {menuItems?.map((element: MenuItem, i: number) => (
                  <Button
                    key={'MenuItems' + i}
                    className='topmenuitem'
                    text={element.label}
                    url={element.url}
                    buttonType={ButtonTypes.TextOnly}
                    buttonVariant={ButtonVariants.Text}
                    buttonSize={ButtonSizes.Small}
                    navigationTrackingEventProps={{
                      navigationItem: element.label,
                      navigationLevel: NavigationLevels.Header,
                    }}
                  />
                ))}
              </ButtonGroup>
              {searchPageUrl?.length && (
                <Button
                  buttonType={ButtonTypes.IconOnly}
                  buttonVariant={ButtonVariants.Text}
                  buttonSize={ButtonSizes.Small}
                  icon={<SearchIcon />}
                  iconPosition={IconPositions.Right}
                  onClick={toggleSearch}
                  aria-label='Toggle search'
                  navigationTrackingEventProps={{
                    navigationLevel: NavigationLevels.Top,
                    navigationItem: 'Search',
                  }}
                />
              )}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Search bar container */}
      <Collapse in={isSearchOpen} animateOpacity>
        <Box
          bg='brand.white'
          pt={0}
          pb={6}
          px={{ base: 5, md: 8, lg: 10 }}
          width='full'
          boxShadow='lg'
          position='absolute'
        >
          <Container maxWidth={'full'} p={0}>
            <HStack spacing={4} width={'100%'} mx='auto'>
              <SearchBar
                searchInputRef={searchInputRef}
                handleSearch={handleSearch}
                trackEvent={{
                  blockName: 'NavigationSearchBar',
                  searchQuery: searchInputRef?.current?.value || '',
                  totalItems: 0,
                }}
                placeHolder={t('search')}
                width='100%'
              />
              <Box>
                <Button
                  buttonType={ButtonTypes.TextOnly}
                  buttonVariant={ButtonVariants.Primary}
                  buttonSize={ButtonSizes.Small}
                  text={t('search')}
                  icon={<SearchIcon />}
                  iconPosition={IconPositions.Right}
                  onClick={handleSearch}
                  aria-label='Search'
                  navigationTrackingEventProps={{
                    navigationLevel: NavigationLevels.Top,
                    navigationItem: 'SearchButton',
                  }}
                />
              </Box>
            </HStack>
          </Container>
        </Box>
      </Collapse>
    </Box>
  )
}
